import { Box, StackProps } from "@chakra-ui/react";
import { motion, useAnimationControls } from "framer-motion";
import { ComponentProps, useEffect } from "react";
import DatePicker from "../internal/datepicker";
import { LoadingSlide } from "../internal/loading";
import { Location } from "../internal/location";
import { MultiSelect } from "../internal/multi";
import { PairSelect } from "../internal/pair";
import { ShortText } from "../internal/shortText";
import { SingleSelect } from "../internal/single";
import { useQuizSnapshot } from "../internal/state";
import { useSlide } from "./slide";
import { Email } from "../internal/email";
import { SliderSelect } from "../internal/slider";
import { TimePicker } from "../internal/timepicker";
import { useQuizConfig } from "./quizProvider";
import { SelectorValueType } from "../internal/state";
import { SelectorValue } from "./types";

type SelectorProps = {
  maxItems?: number;
  onValueChange?: (value: SelectorValueType) => void;
  onTempValueChange?: (value: SelectorValue) => void;
  onProgressChange?: (value: number) => void;
  /**
   * Overrides the default option click behavior of "single" and "multi"
   * selectors (setting the value, auto-submitting and advancing the slide).
   * Use it in custom scenarios where only the option UI should be reused,
   * e.g. together with `actions.toggleRadioOptionWithoutSubmit`.
   */
  onOptionClick?: (value: SelectorValue) => void;
  /**
   * Props for the root container of the specific selector ("single", "multi"
   * and "pair"), e.g. to add bottom padding above a floating button.
   */
  containerProps?: StackProps;
  /**
   * Color of the "(Choose one)" / "(Choose one or more)" label of the
   * "single", "multi" and "pair" selectors.
   */
  labelColor?: string;
  /**
   * Hides the "(Choose one)" / "(Choose one or more)" label of the
   * "single", "multi" and "pair" selectors.
   */
  hideLabel?: boolean;
};

export function Selector({ maxItems, onValueChange, onTempValueChange, onProgressChange, onOptionClick, containerProps, labelColor, hideLabel, ...props }: ComponentProps<typeof Box> & SelectorProps) {
  const slide = useSlide();
  const quizConfig = useQuizConfig();
  const slideState = useQuizSnapshot().currentSlideState;
  const currentSlide = useQuizSnapshot().currentSlideID;

  const controls = useAnimationControls();

  const isActive = slide.id === currentSlide;

  const currentValue = slideState && "value" in slideState ? slideState.value : undefined;

  useEffect(() => {
    if (!isActive || !onValueChange || currentValue === undefined) {
      return;
    }
    onValueChange(currentValue as SelectorValueType);
  }, [isActive, currentValue, onValueChange]);

  useEffect(() => {
    if (!isActive || !slideState) {
      return;
    }
    if (!slideState.isValid && slideState.attempts > 0) {
      handleFlash();
    }
  }, [isActive, slideState, slideState?.attempts]);

  const handleFlash = async () => {
    await controls.start({
      scale: 0.9,
      rotate: 5,
      transition: { duration: 0.1 },
    });
    await controls.start({
      scale: 1,
      rotate: 0,
      transition: {
        scale: { type: "spring", stiffness: 300 },
        rotate: { type: "spring", stiffness: 300 },
      },
    });
  };

  function getSpecificSelector() {
    switch (slide.type) {
      case "short-text": {
        return <ShortText {...slide} />;
      }
      case "single": {
        return <SingleSelect {...slide} maxItems={maxItems} onOptionClick={onOptionClick} containerProps={containerProps} labelColor={labelColor} hideLabel={hideLabel} />;
      }
      case "multi": {
        return <MultiSelect {...slide} maxItems={maxItems} onOptionClick={onOptionClick} containerProps={containerProps} labelColor={labelColor} hideLabel={hideLabel} />;
      }
      case "pair": {
        return <PairSelect {...slide} containerProps={containerProps} labelColor={labelColor} hideLabel={hideLabel} />;
      }
      case "date": {
        return <DatePicker {...slide} />;
      }
      case "time": {
        return <TimePicker {...slide} />;
      }
      case "location": {
        if (!quizConfig.locationApiKey) {
          console.error("Failed to render <Location /> selector: location API key is not provided");
          return null;
        }

        return <Location {...slide} locationApiKey={quizConfig.locationApiKey} />;
      }
      case "loading": {
        return <LoadingSlide {...slide} />;
      }
      case "email": {
        return <Email {...slide} />;
      }
      case "slider": {
        return <SliderSelect {...slide} onTempValueChange={onTempValueChange} onProgressChange={onProgressChange} />;
      }
    }
  }

  if (!slideState) {
    return null;
  }

  return (
    <Box as={motion.div} width={"full"} animate={controls} {...props}>
      {getSpecificSelector()}
    </Box>
  );
}
